import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import { SportType } from '../../../models/court/dto/court.dto';
import {
    CreateOrganizationSportGameDurationDto,
    GetOrganizationSportGameDurationsDto,
    OrganizationSportGameDurationDto,
    UpdateOrganizationSportGameDurationDto,
} from '../../../models/organization/dto/organization-sport-game-duration.dto';
import { DurationWriteWarningSource } from '../../game-duration-management/utils/duration-write-warnings';

/**
 * A duration write echoes the duration itself plus the advisory pricing-impact
 * siblings the API added for the duration axis. The write always succeeded —
 * see `duration-write-warnings.ts` for why an offered-but-unpriced duration is
 * an outage rather than a mispricing.
 */
export type GameDurationWriteResponse = OrganizationSportGameDurationDto &
  DurationWriteWarningSource;

/** Delete returns no duration — only the message and the impact siblings. */
export type GameDurationDeleteResponse = DurationWriteWarningSource & {
  message?: string;
};

/**
 * Keeps the advisory fields out of the entity cache. They describe ONE write,
 * not the row, so caching them would make a stale warning readable as the
 * duration's current state.
 */
const withoutWriteWarnings = (
  data: GameDurationWriteResponse
): OrganizationSportGameDurationDto => {
  const { unpricedDurationMinutes, strandedPricingRows, ...duration } = data;
  return duration;
};

// Query keys
export const ORGANIZATION_GAME_DURATIONS_QUERY_KEY = (organizationId: string, sport?: SportType) => [
  'organizations',
  organizationId,
  'game-durations',
  sport,
];

export const ORGANIZATION_GAME_DURATION_QUERY_KEY = (organizationId: string, durationId: string) => [
  'organizations',
  organizationId,
  'game-durations',
  durationId,
];

// Get all game durations for an organization
export const useGetOrganizationGameDurations = (
  organizationId: string,
  filters?: GetOrganizationSportGameDurationsDto
) => {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ORGANIZATION_GAME_DURATIONS_QUERY_KEY(organizationId, filters?.sport),
    queryFn: async (): Promise<OrganizationSportGameDurationDto[]> => {
      const params = new URLSearchParams();
      if (filters?.sport) {
        params.append('sport', filters.sport);
      }

      return apiClient.fetchApi<OrganizationSportGameDurationDto[], {}>({
        method: 'GET',
        endpoint: `organizations/${organizationId}/game-durations?${params}`,
      });
    },
    enabled: !!organizationId,
  });
};

// Get a single game duration
export const useGetOrganizationGameDuration = (organizationId: string, durationId: string) => {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ORGANIZATION_GAME_DURATION_QUERY_KEY(organizationId, durationId),
    queryFn: async (): Promise<OrganizationSportGameDurationDto> => {
      return apiClient.fetchApi<OrganizationSportGameDurationDto, {}>({
        method: 'GET',
        endpoint: `organizations/${organizationId}/game-durations/${durationId}`,
      });
    },
    enabled: !!organizationId && !!durationId,
  });
};

// Create a new game duration
export const useCreateOrganizationGameDuration = (
  organizationId: string,
  // `GameDurationWriteResponse`, NOT the bare duration: `mutationFn` returns the
  // advisory impact siblings and `onSuccess?.(data)` below hands them straight
  // to this callback, so typing the parameter as the plain row told every
  // consumer the warnings were not there. Because `GameDurationPricingImpactDto`
  // is a weak type (every field optional), TypeScript answers that with TS2559
  // — "no properties in common" — at the `buildDurationWriteWarning(result)`
  // call sites, and `react-scripts build` refuses to compile the dashboard at
  // all. It is not caught by `npx tsc --noEmit` in this project (it resolves a
  // different TypeScript), which is why it survived: gate on the DEPLOY BUILD.
  onSuccess?: (data: GameDurationWriteResponse) => void
) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['createOrganizationGameDuration', organizationId],
    mutationFn: async (body: CreateOrganizationSportGameDurationDto): Promise<GameDurationWriteResponse> => {
      return apiClient.fetchApi<GameDurationWriteResponse, CreateOrganizationSportGameDurationDto>({
        method: 'POST',
        endpoint: `organizations/${organizationId}/game-durations`,
        body,
      });
    },
    onSuccess: (data) => {
      // Invalidate all game durations queries for this organization
      queryClient.invalidateQueries({
        queryKey: ['organizations', organizationId, 'game-durations'],
      });
      onSuccess?.(data);
    },
  });
};

// Update a game duration
export const useUpdateOrganizationGameDuration = (
  organizationId: string,
  durationId: string,
  // Same as create above — the update rail reports BOTH halves at once (the old
  // value's rows are stranded and the new value may be unpriced), so this is
  // the callback that most needed the fields it declared away.
  onSuccess?: (data: GameDurationWriteResponse) => void
) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['updateOrganizationGameDuration', organizationId, durationId],
    mutationFn: async (body: UpdateOrganizationSportGameDurationDto): Promise<GameDurationWriteResponse> => {
      return apiClient.fetchApi<GameDurationWriteResponse, UpdateOrganizationSportGameDurationDto>({
        method: 'PUT',
        endpoint: `organizations/${organizationId}/game-durations/${durationId}`,
        body,
      });
    },
    onSuccess: (data) => {
      // Update the specific duration in cache
      queryClient.setQueryData(
        ORGANIZATION_GAME_DURATION_QUERY_KEY(organizationId, durationId),
        withoutWriteWarnings(data)
      );
      // Invalidate list queries
      queryClient.invalidateQueries({
        queryKey: ['organizations', organizationId, 'game-durations'],
      });
      onSuccess?.(data);
    },
  });
};

// Delete a game duration
export const useDeleteOrganizationGameDuration = (
  organizationId: string,
  onSuccess?: (data: GameDurationDeleteResponse) => void
) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['deleteOrganizationGameDuration', organizationId],
    mutationFn: async (durationId: string): Promise<GameDurationDeleteResponse> => {
      return apiClient.fetchApi<GameDurationDeleteResponse, {}>({
        method: 'DELETE',
        endpoint: `organizations/${organizationId}/game-durations/${durationId}`,
      });
    },
    onSuccess: (data, durationId) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: ORGANIZATION_GAME_DURATION_QUERY_KEY(organizationId, durationId),
      });
      // Invalidate list queries
      queryClient.invalidateQueries({
        queryKey: ['organizations', organizationId, 'game-durations'],
      });
      onSuccess?.(data);
    },
  });
};

// Get available durations for a specific sport
export const useGetAvailableGameDurationsForSport = (
  organizationId: string,
  sport: SportType
) => {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['organizations', organizationId, 'sports', sport, 'game-durations'],
    queryFn: async (): Promise<{ durations: number[] }> => {
      return apiClient.fetchApi<{ durations: number[] }, {}>({
        method: 'GET',
        endpoint: `organizations/${organizationId}/sports/${sport}/game-durations`,
      });
    },
    enabled: !!organizationId && !!sport,
  });
};